/** Nur die Wortmarke, exakt zugeschnitten (gemessene Bounding-Box, 09.08.2026), neben dem Editor-Symbol. */
const WORDMARK = '/landing/emonse-wordmark-white.svg'

/**
 * Faerbung des Logos nach dem Grund, auf dem es steht:
 * - 'light': weisser Grund, Wortmarke schwarz (wie im Editor selbst per brightness(0))
 * - 'dark': dunkler Grund, Wortmarke weiss (die Datei unveraendert)
 * - 'auto': folgt dem Thema der App (hell schwarz, `.dark` weiss)
 */
type EditorBrandTone = 'light' | 'dark' | 'auto'

/**
 * Das Marken-Logo des emonse EDITORs, 1:1 aus `apps/editor/app/components/top-bar.tsx`
 * + `editor.css` (.ed-brand*): Drei-Schichten-Symbol, die Original-Wortmarke,
 * Trennstrich mit Verlauf, "EDITOR" in Fredoka mit demselben Verlauf, und der
 * gemaechliche Licht-Sweep darueber (sp-brand-shine, Kopie von ed-brand-shine).
 *
 * Ein Zuhause fuer drei Orte (21.09.2026): das Editor-Mockup der Landing (weisser
 * Grund), der dunkle Landing-Abschnitt und der Einstieg in den Editor im
 * ShopPanel-Bereich Web Sitesi (folgt dem Thema). Vorher lag der Baustein im
 * Landing-Mockup und der Web-Sitesi-Bereich zeigte nur einen Reiter „Tasarım".
 *
 * Eigene Wortmarken-Datei statt Beschneidungsfenster: der Editor blendet das
 * Symbol mit `overflow:hidden` + negativem Rand aus, und dieses Fenster ist
 * rechts 9 Einheiten zu schmal (gemessen 09.08.2026: Wortmarke laeuft bis 245.39,
 * sichtbar war nur bis 236). `emonse-wordmark-white.svg` traegt exakt die
 * gemessene Bounding-Box, damit kann nichts mehr abgeschnitten werden.
 */
export const EditorBrand = ({
  scale = 1,
  tone = 'light',
}: {
  scale?: number
  tone?: EditorBrandTone
}) => (
  <span
    className="sp-brand-shine"
    aria-label="emonse EDITOR"
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 9 * scale,
      whiteSpace: 'nowrap',
      position: 'relative',
      overflow: 'hidden',
      padding: `${2 * scale}px ${4 * scale}px`,
    }}
  >
    <svg
      width={26 * scale}
      height={26 * scale}
      viewBox="0 0 34 34"
      fill="none"
      aria-hidden="true"
      style={{ flexShrink: 0 }}
    >
      <rect x="4" y="4" width="26" height="7.5" rx="3.75" fill="#ff6061" />
      <rect x="4" y="13.5" width="26" height="7.5" rx="3.75" fill="#9ec9ff" />
      <rect x="4" y="23" width="20" height="7.5" rx="3.75" fill="#bcd9ff" />
    </svg>
    <img
      src={WORDMARK}
      alt=""
      className={tone === 'auto' ? 'brightness-0 dark:brightness-100' : undefined}
      style={{
        height: 21 * scale,
        display: 'block',
        flexShrink: 0,
        filter: tone === 'dark' ? 'none' : tone === 'light' ? 'brightness(0)' : undefined,
      }}
    />
    <span
      style={{
        width: 3 * scale,
        height: 20 * scale,
        borderRadius: 3,
        background: 'linear-gradient(90deg, #c26bff, #6a8bff 55%, #4fd8ff)',
        flexShrink: 0,
      }}
    />
    <span
      style={{
        fontFamily: "'Fredoka', 'Inter', sans-serif",
        fontWeight: 700,
        fontSize: 16 * scale,
        letterSpacing: '0.1em',
        background: 'linear-gradient(90deg, #c26bff, #6a8bff 55%, #4fd8ff)',
        WebkitBackgroundClip: 'text',
        backgroundClip: 'text',
        color: 'transparent',
      }}
    >
      EDITOR
    </span>
    {/* Lichtband: eigenes Element statt ::after, damit es inline lebt */}
    <span
      aria-hidden
      className={tone === 'auto' ? 'mix-blend-normal dark:mix-blend-overlay' : undefined}
      style={{
        position: 'absolute',
        inset: 0,
        background:
          'linear-gradient(105deg, transparent 36%, rgba(255,255,255,0.85) 50%, transparent 64%)',
        transform: 'translateX(-140%)',
        animation: 'sp-brand-shine 10s linear infinite',
        pointerEvents: 'none',
        mixBlendMode: tone === 'dark' ? 'overlay' : tone === 'light' ? 'normal' : undefined,
      }}
    />
  </span>
)
